import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { FileText, CheckCircle2, XCircle, Pause, IndianRupee, Eye, X, Send, Building2, User as UserIcon } from 'lucide-react';

const fmt = v => `\u20B9${Number(v || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const STATUS_FILTERS = [
  { k: 'submitted', l: 'New', color: 'amber' },
  { k: 'on_hold', l: 'On Hold', color: 'orange' },
  { k: 'approved', l: 'Approved', color: 'blue' },
  { k: 'paid', l: 'Paid', color: 'emerald' },
  { k: 'rejected', l: 'Rejected', color: 'red' },
  { k: 'all', l: 'All', color: 'zinc' },
];

/**
 * Admin / Accountant dashboard widget — shows incoming invoices from
 * vendors and freelancers, lets the admin review, approve, hold, reject,
 * or mark as paid. Triggers email notifications to the vendor automatically
 * via the backend `/invoices/{id}/status` endpoint.
 */
export default function AdminInvoicesInbox() {
  const { api } = useAuth();
  const [invoices, setInvoices] = useState([]);
  const [stats, setStats] = useState(null);
  const [filter, setFilter] = useState('submitted');
  const [openInvoice, setOpenInvoice] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [vendorKey, setVendorKey] = useState('');

  const load = () => {
    const p = new URLSearchParams();
    if (filter !== 'all') p.set('status', filter);
    if (vendorKey) p.set('vendor_key', vendorKey);
    const qs = p.toString();
    api.get(`/invoices${qs ? `?${qs}` : ''}`).then(r => setInvoices(r.data || [])).catch(() => setInvoices([]));
    api.get('/invoices/stats/admin').then(r => setStats(r.data)).catch(() => {});
  };
  useEffect(load, [api, filter, vendorKey]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    api.get('/invoices/vendors/summary').then(r => setVendors(r.data?.vendors || [])).catch(() => setVendors([]));
  }, [api]);

  const selectedVendor = vendors.find(v => v.key === vendorKey) || null;

  const totalDue = useMemo(() => invoices.filter(i => i.status === 'submitted' || i.status === 'on_hold').reduce((s, i) => s + (i.net_payable || i.total_amount || 0), 0), [invoices]);

  const updateStatus = async (inv, nextStatus, opts = {}) => {
    if (nextStatus === 'rejected' && !opts.note) {
      const note = prompt(`Reason for rejecting ${inv.invoice_no}:`);
      if (!note) return;
      opts.note = note;
    }
    if (nextStatus === 'paid' && !opts.payment_ref) {
      const ref = prompt(`Payment reference (UTR / Transaction ID) for ${inv.invoice_no}:`, '');
      if (ref === null) return;
      opts.payment_ref = ref || '';
      opts.payment_method = 'NEFT';
    }
    try {
      await api.put(`/invoices/${inv.id}/status`, { status: nextStatus, ...opts });
      load();
      setOpenInvoice(null);
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to update status');
    }
  };

  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded-md" data-testid="admin-invoices-inbox">
      <div className="px-4 py-3 border-b border-zinc-800 flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <FileText className="w-4 h-4 text-amber-400" />
          <h3 className="text-xs font-semibold text-zinc-300 uppercase tracking-wider">Invoices Inbox</h3>
          {stats?.submitted?.count > 0 && (
            <span className="text-[10px] bg-amber-500/20 text-amber-400 px-2 py-0.5 rounded-full font-bold">
              {stats.submitted.count} pending review · {fmt(stats.pending_review_amount)}
            </span>
          )}
        </div>
        <div className="flex items-center gap-1.5 flex-wrap">
          <select value={vendorKey} onChange={e => setVendorKey(e.target.value)} data-testid="invoice-vendor-filter"
            className="bg-zinc-950 border border-zinc-800 rounded-sm text-[10px] text-zinc-300 px-2 py-1 outline-none focus:border-amber-500/60 max-w-[240px]">
            <option value="">All vendors ({vendors.length})</option>
            {vendors.map(v => (
              <option key={v.key} value={v.key}>{v.name} ({v.count})</option>
            ))}
          </select>
          {vendorKey && (
            <button onClick={() => setVendorKey('')} data-testid="invoice-vendor-clear"
              className="text-[10px] px-2 py-1 rounded-sm bg-zinc-800 text-zinc-300 hover:text-white flex items-center gap-1">
              <X className="w-2.5 h-2.5" /> Clear
            </button>
          )}
          {STATUS_FILTERS.map(s => {
            const cnt = s.k === 'all' ? invoices.length : (stats?.[s.k]?.count || 0);
            return (
              <button key={s.k} onClick={() => setFilter(s.k)} data-testid={`invoice-filter-${s.k}`}
                className={`text-[10px] px-2.5 py-1 rounded-sm transition-colors ${
                  filter === s.k ? 'bg-amber-500 text-zinc-950 font-bold' : 'bg-zinc-950 border border-zinc-800 text-zinc-400 hover:text-white'
                }`}>
                {s.l} {cnt > 0 && <span className="ml-1 opacity-70">{cnt}</span>}
              </button>
            );
          })}
        </div>
      </div>

      {selectedVendor && (
        <div className="px-4 py-2.5 bg-zinc-950/60 border-b border-zinc-800 flex items-center gap-x-5 gap-y-1 flex-wrap" data-testid="invoice-vendor-summary">
          <div className="min-w-0">
            <p className="text-[11px] text-white truncate">{selectedVendor.name}</p>
            <p className="text-[9px] text-zinc-500 font-mono">
              {selectedVendor.pan ? `PAN ${selectedVendor.pan}` : 'PAN not on file'}{selectedVendor.gstin ? ` · GSTIN ${selectedVendor.gstin}` : ''}
            </p>
          </div>
          <Stat label="Invoices" value={selectedVendor.count} />
          <Stat label="Gross billed" value={fmt(selectedVendor.gross)} tone="text-amber-400" />
          <Stat label="Paid (net)" value={fmt(selectedVendor.paid_amount)} tone="text-emerald-400" />
          <Stat label="Pending (net)" value={fmt(selectedVendor.pending_amount)} tone="text-orange-400" />
          <Stat label="TDS withheld" value={fmt(selectedVendor.tds)} tone="text-rose-300" />
        </div>
      )}

      {invoices.length === 0 ? (
        <div className="px-4 py-10 text-center" data-testid="invoices-empty">
          <FileText className="w-8 h-8 text-zinc-700 mx-auto mb-2" />
          <p className="text-xs text-zinc-500">
            No invoices in "{STATUS_FILTERS.find(s => s.k === filter)?.l}"{selectedVendor ? ` for ${selectedVendor.name}` : ''}
          </p>
        </div>
      ) : (
        <div className="divide-y divide-zinc-800/30">
          {invoices.map(inv => (
            <div key={inv.id} className="px-4 py-3 hover:bg-zinc-900/40 flex items-center gap-3" data-testid={`admin-invoice-${inv.id}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs text-white font-mono">{inv.invoice_no}</span>
                  <span className="text-[10px] text-zinc-500">·</span>
                  <span className="text-[11px] text-zinc-300 truncate">{inv.raised_by_name || inv.raised_by_email}</span>
                  <span className="text-[9px] text-zinc-600">{inv.raised_by_role === 'freelancer' ? <UserIcon className="w-2.5 h-2.5 inline" /> : <Building2 className="w-2.5 h-2.5 inline" />}</span>
                </div>
                <p className="text-[10px] text-zinc-500 mt-0.5 truncate">
                  {inv.work_order_no} · {inv.project_name} {inv.milestone ? `· ${inv.milestone}` : ''}
                </p>
              </div>
              <div className="text-right">
                <p className="text-sm font-mono text-amber-400 font-bold">{fmt(inv.total_amount)}</p>
                <p className="text-[9px] text-zinc-600">incl. {inv.gst_percent}% GST</p>
                {inv.tds_amount > 0 && (
                  <p className="text-[9px] text-emerald-400 mt-0.5" data-testid={`invoice-net-payable-${inv.id}`}>
                    − TDS {fmt(inv.tds_amount)} → Net <span className="font-mono font-semibold">{fmt(inv.net_payable)}</span>
                  </p>
                )}
              </div>
              <button onClick={() => setOpenInvoice(inv)} data-testid={`view-invoice-${inv.id}`} className="h-7 px-3 bg-zinc-800 hover:bg-zinc-700 text-zinc-300 text-[10px] rounded-sm flex items-center gap-1.5">
                <Eye className="w-3 h-3" /> Review
              </button>
            </div>
          ))}
          {invoices.length > 0 && (
            <div className="px-4 py-2 bg-zinc-950/40 flex justify-between text-[10px] text-zinc-500">
              <span>{invoices.length} invoices</span>
              <span>Pending due: <span className="text-amber-400 font-mono">{fmt(totalDue)}</span></span>
            </div>
          )}
        </div>
      )}

      {/* Detail modal */}
      {openInvoice && (
        <InvoiceDetailModal invoice={openInvoice} onClose={() => setOpenInvoice(null)} onAction={updateStatus} />
      )}
    </div>
  );
}

function InvoiceDetailModal({ invoice, onClose, onAction }) {
  const inv = invoice;
  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid="invoice-detail-modal">
      <div className="bg-zinc-950 border border-zinc-800 rounded-md w-full max-w-3xl max-h-[90vh] flex flex-col">
        <header className="flex items-center justify-between px-5 py-3 border-b border-zinc-800">
          <div className="flex items-center gap-2">
            <FileText className="w-4 h-4 text-amber-400" />
            <h3 className="text-sm font-heading font-bold text-white">{inv.invoice_no}</h3>
            <span className="text-[10px] uppercase px-2 py-0.5 rounded-full bg-zinc-800 text-zinc-300 font-bold" data-testid="invoice-detail-status">{inv.status?.replace('_', ' ')}</span>
          </div>
          <button onClick={onClose} className="text-zinc-500 hover:text-white"><X className="w-4 h-4" /></button>
        </header>

        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {/* Bill From / Bill To header — required for Indian B2B invoices.
              The studio profile is snapshotted on the invoice at creation
              time so historical invoices keep showing the GSTIN/address
              from when they were raised. */}
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-3" data-testid="bill-from-block">
              <p className="text-[10px] uppercase text-zinc-500 tracking-widest">Bill From (Vendor)</p>
              <p className="text-xs text-white font-semibold mt-1">
                {inv.billed_from?.name || inv.raised_by_name || inv.raised_by_email}
              </p>
              {inv.billed_from?.contact_person && (
                <p className="text-[10px] text-zinc-400 mt-0.5">Contact: {inv.billed_from.contact_person}</p>
              )}
              <p className="text-[10px] text-zinc-400 mt-0.5">{inv.billed_from?.email || inv.raised_by_email}</p>
              {inv.billed_from?.address_line_1 && (
                <p className="text-[10px] text-zinc-400 mt-0.5 leading-tight">
                  {inv.billed_from.address_line_1}
                  {inv.billed_from.address_line_2 ? `, ${inv.billed_from.address_line_2}` : ''}
                  <br />
                  {inv.billed_from.city} ({inv.billed_from.state_name || ''}) {inv.billed_from.pin}
                </p>
              )}
              {(inv.billed_from?.gstin || inv.vendor_gstin) && (
                <p className="text-[10px] text-zinc-300 mt-1 font-mono" data-testid="vendor-gstin">
                  GSTIN: {inv.billed_from?.gstin || inv.vendor_gstin}
                  {inv.billed_from?.pan ? ` · PAN: ${inv.billed_from.pan}` : ''}
                </p>
              )}
              {inv.billed_from?.constitution && (
                <p className="text-[9px] text-zinc-500 mt-0.5 italic">{inv.billed_from.constitution}</p>
              )}
            </div>
            <div className="bg-zinc-900 border border-amber-500/20 rounded-sm p-3" data-testid="bill-to-block">
              <p className="text-[10px] uppercase text-amber-400 tracking-widest">Bill To (Studio)</p>
              <p className="text-xs text-white font-semibold mt-1">
                {inv.billed_to?.name || 'Vawworld Animation Studios'}
              </p>
              {inv.billed_to?.address_line_1 && (
                <p className="text-[10px] text-zinc-400 mt-0.5 leading-tight">
                  {inv.billed_to.address_line_1}
                  {inv.billed_to.address_line_2 ? `, ${inv.billed_to.address_line_2}` : ''}
                  <br />
                  {inv.billed_to.city} ({inv.billed_to.state_name || 'Madhya Pradesh'}) {inv.billed_to.pin}
                </p>
              )}
              {inv.billed_to?.gstin && (
                <p className="text-[10px] text-zinc-300 mt-1 font-mono" data-testid="studio-gstin">
                  GSTIN: {inv.billed_to.gstin}
                  {inv.billed_to.pan ? ` · PAN: ${inv.billed_to.pan}` : ''}
                </p>
              )}
            </div>
          </div>

          {/* Header info */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
            <Field label="Raised By" value={inv.raised_by_name || inv.raised_by_email} />
            <Field label="Work Order" value={inv.work_order_no} />
            <Field label="Project" value={inv.project_name} />
            <Field label="Milestone" value={inv.milestone || '—'} />
            <Field label="Invoice Date" value={inv.invoice_date ? new Date(inv.invoice_date).toLocaleDateString() : '—'} />
            <Field label="Submitted" value={inv.submitted_at ? new Date(inv.submitted_at).toLocaleString() : '—'} />
            <Field label="Approved" value={inv.approved_at ? new Date(inv.approved_at).toLocaleString() : '—'} />
            <Field label="Paid" value={inv.paid_at ? new Date(inv.paid_at).toLocaleString() : '—'} />
          </div>

          {/* Line items */}
          <div className="bg-zinc-900 border border-zinc-800 rounded-sm overflow-hidden">
            <table className="w-full text-[11px]">
              <thead className="bg-zinc-950 text-zinc-500">
                <tr>
                  <th className="text-left px-3 py-1.5 font-normal w-[55%]">Description</th>
                  <th className="text-right px-3 py-1.5 font-normal w-[10%]">Qty</th>
                  <th className="text-right px-3 py-1.5 font-normal w-[15%]">Rate</th>
                  <th className="text-right px-3 py-1.5 font-normal w-[20%]">Amount</th>
                </tr>
              </thead>
              <tbody>
                {(inv.line_items || []).map((li, i) => (
                  <tr key={i} className="border-t border-zinc-800/50">
                    <td className="px-3 py-1.5 text-zinc-300">{li.description}</td>
                    <td className="px-3 py-1.5 text-right text-zinc-400 font-mono">{li.quantity}</td>
                    <td className="px-3 py-1.5 text-right text-zinc-400 font-mono">{fmt(li.rate)}</td>
                    <td className="px-3 py-1.5 text-right text-amber-400 font-mono">{fmt(li.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Totals — full TDS breakdown so the admin/CFO sees exactly what
              will be paid out and what's withheld for govt deposit. */}
          <div className="bg-zinc-900 border border-amber-500/30 rounded-sm p-3 space-y-1 text-xs" data-testid="invoice-totals">
            <div className="flex justify-between text-zinc-400"><span>Subtotal (Taxable)</span><span className="font-mono">{fmt(inv.subtotal)}</span></div>
            <div className="flex justify-between text-zinc-400"><span>GST ({inv.gst_percent}%)</span><span className="font-mono">{fmt(inv.gst_amount)}</span></div>
            <div className="flex justify-between text-zinc-200 border-t border-zinc-800 pt-1"><span>Gross Total</span><span className="font-mono">{fmt(inv.total_amount)}</span></div>
            {inv.tds_amount > 0 && (
              <>
                <div className="flex justify-between text-rose-300" data-testid="tds-row">
                  <span>− TDS @ {inv.tds_percent}% (Sec 194J, on taxable)</span>
                  <span className="font-mono">−{fmt(inv.tds_amount)}</span>
                </div>
                <div className="flex justify-between text-emerald-400 font-bold border-t border-zinc-800 pt-1 text-sm" data-testid="net-payable-row">
                  <span>Net Payable</span>
                  <span className="font-mono">{fmt(inv.net_payable)}</span>
                </div>
              </>
            )}
            {!inv.tds_amount && (
              <div className="flex justify-between text-amber-400 font-bold border-t border-zinc-800 pt-1 text-sm">
                <span>Total Due</span>
                <span className="font-mono">{fmt(inv.total_amount)}</span>
              </div>
            )}
          </div>

          {inv.notes && (
            <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-3 text-xs">
              <p className="text-[10px] uppercase text-zinc-500 mb-1">Notes from Vendor</p>
              <p className="text-zinc-300 whitespace-pre-wrap">{inv.notes}</p>
            </div>
          )}

          {inv.payment_ref && (
            <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-sm p-3 text-xs flex items-center gap-3">
              <CheckCircle2 className="w-4 h-4 text-emerald-400" />
              <div>
                <p className="text-[10px] uppercase text-emerald-400">Payment Reference</p>
                <p className="font-mono text-emerald-300">{inv.payment_ref} {inv.payment_method ? `· ${inv.payment_method}` : ''}</p>
              </div>
            </div>
          )}

          {/* History */}
          {inv.status_history?.length > 1 && (
            <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-3 text-[10px]">
              <p className="uppercase text-zinc-500 mb-1.5">Audit Trail</p>
              <div className="space-y-1">
                {inv.status_history.map((h, i) => (
                  <div key={i} className="flex justify-between text-zinc-400">
                    <span><span className="text-amber-400 font-bold uppercase">{h.status?.replace('_', ' ')}</span> by {h.by} {h.note ? `— ${h.note}` : ''}</span>
                    <span className="font-mono text-zinc-600">{h.at?.slice(0, 19).replace('T', ' ')}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <footer className="px-5 py-3 border-t border-zinc-800 flex items-center justify-end gap-2 flex-wrap">
          {inv.status === 'submitted' && (
            <>
              <button onClick={() => onAction(inv, 'rejected')} data-testid="invoice-action-reject" className="h-9 px-4 bg-red-500/15 hover:bg-red-500/25 border border-red-500/30 text-red-400 text-xs rounded-sm flex items-center gap-1.5">
                <XCircle className="w-3.5 h-3.5" /> Reject
              </button>
              <button onClick={() => { const note = prompt('Why hold this invoice?'); if (note) onAction(inv, 'on_hold', { note }); }} data-testid="invoice-action-hold" className="h-9 px-4 bg-orange-500/15 hover:bg-orange-500/25 border border-orange-500/30 text-orange-400 text-xs rounded-sm flex items-center gap-1.5">
                <Pause className="w-3.5 h-3.5" /> Hold
              </button>
              <button onClick={() => onAction(inv, 'approved')} data-testid="invoice-action-approve" className="h-9 px-4 bg-blue-500 hover:bg-blue-400 text-white text-xs font-bold rounded-sm flex items-center gap-1.5">
                <CheckCircle2 className="w-3.5 h-3.5" /> Approve for Payment
              </button>
            </>
          )}
          {inv.status === 'on_hold' && (
            <>
              <button onClick={() => onAction(inv, 'rejected')} data-testid="invoice-action-reject" className="h-9 px-4 bg-red-500/15 hover:bg-red-500/25 border border-red-500/30 text-red-400 text-xs rounded-sm flex items-center gap-1.5">
                <XCircle className="w-3.5 h-3.5" /> Reject
              </button>
              <button onClick={() => onAction(inv, 'approved')} data-testid="invoice-action-approve" className="h-9 px-4 bg-blue-500 hover:bg-blue-400 text-white text-xs font-bold rounded-sm flex items-center gap-1.5">
                <CheckCircle2 className="w-3.5 h-3.5" /> Approve
              </button>
            </>
          )}
          {inv.status === 'approved' && (
            <button onClick={() => onAction(inv, 'paid')} data-testid="invoice-action-pay" className="h-9 px-5 bg-emerald-500 hover:bg-emerald-400 text-zinc-950 text-xs font-bold rounded-sm flex items-center gap-1.5">
              <IndianRupee className="w-3.5 h-3.5" /> Mark as Paid
            </button>
          )}
          {(inv.status === 'paid' || inv.status === 'rejected') && (
            <p className="text-[11px] text-zinc-500 italic">This invoice is in a terminal state ({inv.status}) and cannot be modified.</p>
          )}
        </footer>
      </div>
    </div>
  );
}

function Stat({ label, value, tone = 'text-zinc-300' }) {
  return (
    <div>
      <p className="text-[9px] uppercase text-zinc-600 tracking-wider">{label}</p>
      <p className={`text-[11px] font-mono ${tone}`}>{value}</p>
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <p className="text-[9px] text-zinc-600 uppercase">{label}</p>
      <p className="text-xs text-zinc-300">{value || '—'}</p>
    </div>
  );
}
